/**
 * Handlers for template operations (save, load, delete, duplicate)
 */

export const createTemplateHandlers = (
  setLocalEdits, 
  setHasUnsavedChanges, 
  showToast, 
  saveTemplate, 
  loadTemplate, 
  deleteTemplate, 
  duplicateTemplate,
  localEdits
) => {
  /**
   * Handle saving a template
   */
  const handleSaveTemplate = (templateData) => {
    console.log('Saving template:', templateData);
    try {
      const template = {
        ...templateData,
        localEdits: localEdits
      };
      localStorage.setItem(`template_${templateData.name}`, JSON.stringify(template));
      showToast('Template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      showToast('Failed to save template', true);
    }
  };

  /**
   * Handle loading a template
   */
  const handleLoadTemplate = (template) => {
    console.log('Loading template:', template);
    try {
      // Update local edits from template
      if (template.localEdits) {
        setLocalEdits(template.localEdits);
      } else if (template.editHistory?.present) {
        // Handle legacy templates that used editHistory
        const newEdits = {};
        
        // Get all product IDs from the template
        Object.keys(template.editHistory.present).forEach(productId => {
          const product = template.editHistory.present[productId];
          
          // Add product changes to localEdits
          newEdits[productId] = {
            ...product,
            _timestamp: Date.now()
          };
        });
        
        setLocalEdits(newEdits);
      }
      
      setHasUnsavedChanges(true);
      showToast('Template loaded successfully');
    } catch (error) {
      console.error('Error loading template:', error);
      showToast('Failed to load template', true);
    }
  };

  /**
   * Handle deleting a template
   */
  const handleDeleteTemplate = (templateId) => {
    console.log('Deleting template:', templateId);
    try {
      localStorage.removeItem(templateId);
      showToast('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Failed to delete template', true);
    }
  };

  /**
   * Handle duplicating a template
   */
  const handleDuplicateTemplate = (templateId) => {
    console.log('Duplicating template:', templateId);
    try {
      const template = JSON.parse(localStorage.getItem(templateId));
      if (template) {
        const newName = `${template.name} (Copy)`;
        const newTemplate = {
          ...template,
          name: newName
        };
        localStorage.setItem(`template_${newName}`, JSON.stringify(newTemplate));
        showToast('Template duplicated successfully');
      } else {
        throw new Error('Template not found');
      }
    } catch (error) {
      console.error('Error duplicating template:', error);
      showToast('Failed to duplicate template', true);
    }
  };

  /**
   * Handle applying template updates to a product
   */
  const handleTemplateUpdate = (productId, updates) => {
    console.log(`Applying template updates to product ${productId}:`, updates);
    
    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };
        
        // Initialize product in localEdits if needed
        if (!updatedEdits[productId]) {
          updatedEdits[productId] = { _timestamp: Date.now() };
        }
        
        // Apply each update to the product
        Object.entries(updates).forEach(([field, value]) => {
          // Handle variant updates specially
          if (field === 'variant_updates') {
            if (!updatedEdits[productId].variant_updates) {
              updatedEdits[productId].variant_updates = {};
            }
            
            // Merge variant updates
            Object.entries(value).forEach(([variantId, variantUpdates]) => {
              if (!updatedEdits[productId].variant_updates[variantId]) {
                updatedEdits[productId].variant_updates[variantId] = {};
              }
              
              Object.entries(variantUpdates).forEach(([variantField, variantValue]) => {
                updatedEdits[productId].variant_updates[variantId][variantField] = variantValue;
              });
            });
          } 
          // Handle metafield updates specially
          else if (field === 'metafields') {
            if (!updatedEdits[productId].metafields) {
              updatedEdits[productId].metafields = {};
            }
            
            // Merge metafield updates
            Object.entries(value).forEach(([metafieldId, metafieldValue]) => {
              updatedEdits[productId].metafields[metafieldId] = metafieldValue;
            });
          }
          // Handle options updates specially
          else if (field === 'options') {
            if (!updatedEdits[productId].options) {
              updatedEdits[productId].options = [];
            }
            
            // Merge options updates
            value.forEach((option, index) => {
              // Ensure the option exists at the specified index
              while (updatedEdits[productId].options.length <= index) {
                updatedEdits[productId].options.push({});
              }
              
              // Update option fields
              Object.entries(option).forEach(([optionField, optionValue]) => {
                updatedEdits[productId].options[index][optionField] = optionValue;
              });
            });
          }
          // Handle all other fields
          else if (field !== '_timestamp') {
            updatedEdits[productId][field] = value;
          }
        });
        
        // Update timestamp to force re-render
        updatedEdits[productId]._timestamp = Date.now();
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
      showToast('Template applied successfully');
    } catch (error) {
      console.error('Error applying template updates:', error);
      showToast('Failed to apply template', true);
    }
  };

  return {
    handleSaveTemplate,
    handleLoadTemplate,
    handleDeleteTemplate,
    handleDuplicateTemplate,
    handleTemplateUpdate
  };
};
